// src/pages/OrderLookupByPhone.jsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* -------- Base API -------- */
function getApiBase() {
  const env = (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "");
  if (env) return env;
  if (typeof window !== "undefined" && window.location.hostname === "localhost") {
    return "http://localhost:5000";
  }
  return "";
}
const API_BASE = getApiBase();

/* -------- Utils -------- */
const normalizeTogoPhone = (raw = "") => {
  const d = String(raw || "").replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("228")) return d.slice(3);
  if (d.length === 8) return d;
  return null;
};

const fmtMoney = (v) =>
  `${new Intl.NumberFormat("fr-FR").format(Math.round(Number(v || 0)))} FCFA`;

const fmtDate = (iso = "") => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso.replace("T", " ").replace("Z", "");
    return new Intl.DateTimeFormat("fr-FR", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(d);
  } catch {
    return iso;
  }
};

function StatusBadge({ status }) {
  const s = String(status || "").toLowerCase();
  const map = {
    paid: "success",
    pending: "warning",
    processing: "warning",
    awaiting_payment_offline: "secondary",
    failed: "danger",
    canceled: "secondary",
    cancelled: "secondary",
  };
  const bs = map[s] || "secondary";
  const label =
    s === "paid"
      ? "Payée"
      : s === "pending"
        ? "En attente"
        : s === "processing"
          ? "En cours"
          : s === "awaiting_payment_offline"
            ? "En attente (offline)"
            : s === "failed"
              ? "Échouée / Refusée"
              : s === "canceled" || s === "cancelled"
                ? "Annulée"
                : status || "Inconnu";
  return <span className={`badge text-bg-${bs}`}>{label}</span>;
}

function ProviderBadge({ provider }) {
  const p = String(provider || "").toLowerCase();
  const map = {
    tmoney: { label: "TMoney", bs: "info", icon: "bi-phone" },
    flooz: { label: "Flooz", bs: "info", icon: "bi-phone" },
    offline: { label: "Offline", bs: "secondary", icon: "bi-receipt" },
  };
  const { label, bs, icon } = map[p] || { label: provider || "—", bs: "light", icon: "bi-question-circle" };
  return (
    <span className={`badge text-bg-${bs}`}>
      <i className={`bi ${icon} me-1`}></i>
      {label}
    </span>
  );
}

/* -------- Page -------- */
export default function OrderLookupByPhone() {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [err, setErr] = useState("");

  const phone8 = useMemo(() => normalizeTogoPhone(phone), [phone]);
  const isValid = Boolean(phone8);

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    setOrders([]);

    if (!isValid) {
      setErr("Saisissez un numéro TG valide (8 chiffres ou +228XXXXXXXX).");
      return;
    }

    try {
      setLoading(true);
      // on envoie la version normalisée à 8 chiffres pour éviter les ambiguïtés backend
      const url = `${API_BASE}/api/orders/by-phone?phone=${encodeURIComponent(phone8)}`;
      const r = await fetch(url);
      const txt = await r.text();
      let data;
      try {
        data = JSON.parse(txt);
      } catch {
        throw new Error(`Réponse non-JSON (${r.status})`);
      }
      if (!r.ok || !data?.ok) throw new Error(data?.error || `HTTP ${r.status}`);

      const list = Array.isArray(data.orders) ? data.orders : [];
      // tri défensif : plus récentes → plus anciennes
      list.sort((a, b) => {
        const ta = new Date(a?.created_at || 0).getTime();
        const tb = new Date(b?.created_at || 0).getTime();
        return tb - ta;
      });
      setOrders(list);
    } catch (e2) {
      setErr(e2.message || "Erreur de recherche.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4 py-md-5" style={{ maxWidth: 980 }}>
      {/* Hero */}
      <section
        className="p-3 p-md-4 rounded-3 mb-4 position-relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #eef5ff, #ffffff)",
          border: "1px solid #e6efff",
        }}
      >
        <div className="d-flex flex-column flex-md-row align-items-center gap-3">
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-circle text-primary"
            style={{ width: 60, height: 60, background: "#fff", border: "1px solid #e9eefc" }}
            aria-hidden="true"
          >
            <i className="bi bi-receipt-cutoff" style={{ fontSize: 30 }}></i>
          </div>
          <div className="text-center text-md-start">
            <h3 className="mb-1">Suivre mes commandes</h3>
            <p className="text-muted mb-0">
              Retrouvez vos commandes avec votre numéro Togolais (TMoney / Flooz / Offline).
            </p>
          </div>
        </div>
      </section>

      {/* Formulaire */}
      {/* Formulaire */}
      <form className="mb-3" onSubmit={submit} noValidate>
        <div className="col-12">
          <label className="form-label" htmlFor="phone">Téléphone (TG)</label>
          <div className="input-group">
            <span className="input-group-text" id="addon-phone">
              <i className="bi bi-telephone"></i>
            </span>
            <input
              id="phone"
              className={`form-control ${phone && !isValid ? "is-invalid" : ""}`}
              inputMode="tel"
              aria-describedby="addon-phone phone-help"
              pattern="[0-9+ ]*"
              placeholder="+228 93 41 08 27 ou 93410827"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoFocus
            />
            {phone && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setPhone("")}
                title="Effacer"
                aria-label="Effacer"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !isValid}
            >
              {loading ? (
                <>
                  <i className="bi bi-arrow-repeat spin me-2"></i>
                  Recherche…
                </>
              ) : (
                <>
                  <i className="bi bi-search me-2"></i>
                  Rechercher
                </>
              )}
            </button>
          </div>
          <div id="phone-help" className="form-text">
            Format: 8 chiffres (ex. <code>93410827</code>) ou <code>+228XXXXXXXX</code>.
          </div>
        </div>
      </form>

      {/* Erreur */}
      {err && (
        <div className="alert alert-danger d-flex align-items-center" role="alert" aria-live="assertive">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          <div>{err}</div>
        </div>
      )}

      {/* Résultats */}
      {orders.length > 0 && (
        <div className="card shadow-sm">
          <div className="card-body">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
              <h6 className="m-0" aria-live="polite">
                Commandes ({orders.length})
              </h6>
              <div className="small text-muted">Tri : plus récentes → plus anciennes</div>
            </div>

            {/* Table ≥ md */}
            <div className="d-none d-md-block table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th style={{ minWidth: 160 }}>N°</th>
                    <th>Statut</th>
                    <th>Fournisseur</th>
                    <th>Total</th>
                    <th>Date</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => {
                    const id = o.orderId || o.id;
                    const detailsHref =
                      o.provider === "offline"
                        ? `/checkout/success?orderId=${encodeURIComponent(id)}`
                        : `/checkout/success?identifier=${encodeURIComponent(id)}`;
                    return (
                      <tr key={id}>
                        <td className="fw-semibold text-break">
                          <i className="bi bi-hash me-1 text-muted"></i>
                          <span className="align-middle">{id}</span>
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-decoration-none ms-1 p-0 align-middle"
                            title="Copier"
                            onClick={() => navigator.clipboard?.writeText(id)}
                          >
                            <i className="bi bi-clipboard"></i>
                          </button>
                        </td>
                        <td><StatusBadge status={o.status} /></td>
                        <td><ProviderBadge provider={o.provider} /></td>
                        <td>{fmtMoney(o.amount_xof)}</td>
                        <td className="small text-muted">{fmtDate(o.created_at)}</td>
                        <td className="text-end">
                          <Link to={detailsHref} className="btn btn-sm btn-outline-primary">
                            Détails
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Cartes mobiles < md */}
            <div className="d-md-none">
              <div className="row g-2">
                {orders.map((o) => {
                  const id = o.orderId || o.id;
                  const detailsHref =
                    o.provider === "offline"
                      ? `/checkout/success?orderId=${encodeURIComponent(id)}`
                      : `/checkout/success?identifier=${encodeURIComponent(id)}`;
                  return (
                    <div className="col-12" key={id}>
                      <div className="border rounded p-3 d-flex flex-column gap-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="fw-semibold text-break">
                            <i className="bi bi-hash me-1 text-muted"></i>
                            {id}
                          </div>
                          <StatusBadge status={o.status} />
                        </div>
                        <div className="d-flex justify-content-between small text-muted">
                          <span>Fournisseur</span>
                          <span><ProviderBadge provider={o.provider} /></span>
                        </div>
                        <div className="d-flex justify-content-between small text-muted">
                          <span>Total</span>
                          <span className="fw-semibold text-dark">{fmtMoney(o.amount_xof)}</span>
                        </div>
                        <div className="d-flex justify-content-between small text-muted">
                          <span>Date</span>
                          <span>{fmtDate(o.created_at)}</span>
                        </div>
                        <div className="d-grid">
                          <Link to={detailsHref} className="btn btn-outline-primary btn-sm">
                            Voir les détails
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="small text-muted mt-3">
              Astuce : clique sur <em>Détails</em> pour voir le paiement et les lignes d’articles.
            </div>
          </div>
        </div>
      )}

      {/* État vide */}
      {!loading && !err && orders.length === 0 && (
        <div className="text-center text-muted py-5">
          <div className="mb-2">
            <i className="bi bi-box-seam" style={{ fontSize: 36 }} aria-hidden="true"></i>
          </div>
          <div className="fw-semibold">Aucune commande à afficher</div>
          <div className="small">
            Saisissez votre numéro togolais pour retrouver vos commandes.
          </div>
          <div className="mt-3">
            <Link to="/catalogue" className="btn btn-outline-secondary btn-sm">
              Explorer le catalogue
            </Link>
          </div>
        </div>
      )}

      {/* Overlay de chargement (léger) */}
      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ background: "rgba(255,255,255,.35)", backdropFilter: "blur(1px)", zIndex: 1050 }}
          aria-hidden="true"
        >
          <div className="position-absolute top-50 start-50 translate-middle text-primary">
            <i className="bi bi-arrow-repeat spin" style={{ fontSize: 28 }}></i>
          </div>
        </div>
      )}

      {/* Styles locaux */}
      <style>{`
        .spin { display: inline-block; animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        /* Focus input plus visible */
        .form-control:focus {
          border-color: #86b7fe;
          box-shadow: 0 0 0 .2rem rgba(13,110,253,.15);
        }

        /* Fluidité du formulaire sur petits écrans */
        @media (max-width: 575.98px) {
          .input-group .btn { padding-left: .75rem; padding-right: .75rem; }
        }
      `}</style>
    </div>
  );
}
